import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert, Autocomplete, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, MenuItem, Stack, Tab, Tabs, TablePagination, TextField, Tooltip, Typography,
} from '@mui/material';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { estimateActionPlanPreview } from '../../../api/climateActionPlanApi';
import { listLegalEntities } from '../../../api/legalEntitiesApi';
import { listOffices } from '../../../api/officesApi';
import LabeledField from '../../../shared/components/LabeledField';
import AppDialog from '../../../shared/components/AppDialog';
import { CATEGORY_META, SCOPE_OPTIONS, SUB_CATEGORY_LABELS_BY_CATEGORY } from '../../../shared/constants';
import { SCROLLABLE_SELECT_MENU_PROPS } from '../../../shared/components/selectMenuProps';
import { formatTonne } from '../../../shared/utils/number';
import {
  ACTION_PLAN_CATEGORIES_BY_SCOPE,
  ACTION_PLAN_CONTRIBUTION_TYPES,
  ACTION_PLAN_SCOPE_LABELS,
  ACTION_PLAN_SUB_CATEGORIES_BY_CATEGORY,
  boundaryTypeForScope,
  endFiscalYearOptions,
  startFiscalYearOptions,
} from './actionPlanModel';

const OPERATIONAL = 'OPERATIONAL_REDUCTION';
const RETIREMENT = 'CARBON_CREDIT_RETIREMENT';
const EMISSIONS_REDUCTION = 'EMISSIONS_REDUCTION';
const OTHER_CLIMATE_BENEFIT = 'OTHER_CLIMATE_BENEFIT';

const yearsInRange = (start, end) => {
  const years = [];
  for (let year = Number(start); year <= Number(end); year += 1) years.push(year);
  return years;
};

const TABLE_PAGE_SIZE = 10;

const emptyEstimate = fiscalYear => ({
  fiscalYear,
  reductionTco2ePerYear: '',
  basisNote: '',
});

const roundReduction = (value) => {
  if (value === '' || value == null) return '';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return String(Number(num.toFixed(2)));
};

const emptyDraft = (selectedYear) => ({
  title: '',
  description: '',
  contributionType: EMISSIONS_REDUCTION,
  actionType: OPERATIONAL,
  scope: 'SCOPE_1',
  category: 'GASOLINE',
  subCategory: null,
  boundaryType: 'LEGAL_ENTITY',
  boundaryRefId: '',
  startFiscalYear: Number(selectedYear),
  endFiscalYear: Number(selectedYear) + 2,
  planningEstimates: [],
});

const draftFromPlan = (plan, selectedYear) => {
  if (!plan) return emptyDraft(selectedYear);
  return {
    title: plan.title,
    description: plan.description || '',
    contributionType: plan.accountingClassification === 'OTHER_CLIMATE_IMPACT'
      ? OTHER_CLIMATE_BENEFIT
      : EMISSIONS_REDUCTION,
    actionType: plan.actionType,
    scope: plan.accountingClassification === 'OTHER_CLIMATE_IMPACT'
      ? 'OTHER'
      : (plan.scope === 'NOT_APPLICABLE' ? 'SCOPE_1' : plan.scope),
    category: plan.accountingClassification === 'OTHER_CLIMATE_IMPACT'
      ? 'POLICY'
      : (plan.category === 'NOT_APPLICABLE' ? 'GASOLINE' : plan.category),
    subCategory: plan.accountingClassification === 'OTHER_CLIMATE_IMPACT'
      ? null : plan.subCategory,
    boundaryType: plan.accountingClassification === 'OTHER_CLIMATE_IMPACT'
      ? 'COMPANY' : (plan.boundary?.type || 'LEGAL_ENTITY'),
    boundaryRefId: plan.accountingClassification === 'OTHER_CLIMATE_IMPACT'
      ? '' : (plan.boundary?.refId || ''),
    startFiscalYear: plan.startFiscalYear,
    endFiscalYear: plan.endFiscalYear,
    aiRecommendationId: plan.aiRecommendationId || null,
    planningEstimates: plan.planningEstimates.map(estimate => ({
      fiscalYear: estimate.fiscalYear,
      reductionTco2ePerYear: roundReduction(estimate.reductionTco2ePerYear),
      basisNote: estimate.basisNote || '',
    })),
  };
};

const isRetirement = draft => draft.actionType === RETIREMENT;
const isOtherClimateBenefit = draft => draft.contributionType === OTHER_CLIMATE_BENEFIT;

const categoryOptionsFor = (scope) => (ACTION_PLAN_CATEGORIES_BY_SCOPE[scope] || []).map((value) => {
  const metadata = CATEGORY_META[value];
  const isCategoryOne = /^CAT_1_[1-9]$/.test(value);
  return {
    value,
    label: isCategoryOne
      ? (metadata?.short || value)
      : `${metadata?.short || value} · ${metadata?.label || value}`,
  };
});

/** Create or edit one multi-year action plan with per-year expected estimates. */
export default function ActionPlanDialog({
  canMutate, onClose, onError, onSave, onRefresh,
  open, plan, selectedYear, t,
}) {
  const [tab, setTab] = useState(0);
  const [draft, setDraft] = useState(() => emptyDraft(selectedYear));
  const [previews, setPreviews] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [legalEntities, setLegalEntities] = useState([]);
  const [offices, setOffices] = useState([]);
  const [estimatePage, setEstimatePage] = useState(0);
  const previewTimerRef = useRef(null);
  const previewSequenceRef = useRef(0);
  const identityLocked = plan?.status === 'IN_PROGRESS';

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setDraft(draftFromPlan(plan, selectedYear));
    setPreviews({});
    setSaveError(null);
    setSaving(false);
    setAttemptedSave(false);
    listLegalEntities()
      .then(items => setLegalEntities(Array.isArray(items) ? items : []))
      .catch(() => setLegalEntities([]));
    listOffices()
      .then(items => setOffices(Array.isArray(items) ? items : []))
      .catch(() => setOffices([]));
  }, [open, plan, selectedYear]);

  const setField = (field, value) => {
    setDraft(current => ({ ...current, [field]: value }));
  };

  const setIdentityField = (field, value) => {
    setDraft(current => ({
      ...current,
      [field]: value,
      planningEstimates: [],
    }));
    setPreviews({});
  };

  const setEstimateField = (fiscalYear, field, value) => {
    setDraft(current => {
      const exists = current.planningEstimates.some(estimate => estimate.fiscalYear === fiscalYear);
      if (!exists) {
        return {
          ...current,
          planningEstimates: [
            ...current.planningEstimates,
            { ...emptyEstimate(fiscalYear), [field]: value },
          ],
        };
      }
      return {
        ...current,
        planningEstimates: current.planningEstimates.map(estimate => (
          estimate.fiscalYear === fiscalYear
            ? { ...estimate, [field]: value }
            : estimate
        )),
      };
    });
  };

  const scopeCategoryOptions = useMemo(() => categoryOptionsFor(draft.scope), [draft.scope]);

  const scopeSubCategories = useMemo(() => (
    ACTION_PLAN_SUB_CATEGORIES_BY_CATEGORY[draft.category] || []
  ), [draft.category]);

  const subCategoryMissing = !isRetirement(draft) && !isOtherClimateBenefit(draft)
    && scopeSubCategories.length > 0 && !draft.subCategory;

  const includeYears = useMemo(() => {
    if (!plan) return [];
    return [plan.startFiscalYear, plan.endFiscalYear];
  }, [plan]);

  const startOptions = useMemo(() => startFiscalYearOptions({ includeYears }), [includeYears]);
  const endOptions = useMemo(() => endFiscalYearOptions({
    startFiscalYear: draft.startFiscalYear,
    includeYears,
  }), [draft.startFiscalYear, includeYears]);

  const tableYears = useMemo(() => (
    yearsInRange(draft.startFiscalYear, draft.endFiscalYear)
  ), [draft.startFiscalYear, draft.endFiscalYear]);

  useEffect(() => {
    if (draft.endFiscalYear < draft.startFiscalYear) {
      setDraft(current => ({ ...current, endFiscalYear: current.startFiscalYear }));
    }
  }, [draft.startFiscalYear, draft.endFiscalYear]);

  useEffect(() => {
    setEstimatePage(0);
  }, [draft.startFiscalYear, draft.endFiscalYear]);

  const estimateForYear = (year) => (
    draft.planningEstimates.find(estimate => estimate.fiscalYear === year) || emptyEstimate(year)
  );

  const changeScope = (scope) => {
    const categories = ACTION_PLAN_CATEGORIES_BY_SCOPE[scope] || [];
    setDraft(current => ({
      ...current,
      scope,
      category: categories.length === 1 ? categories[0] : '',
      subCategory: null,
      boundaryType: boundaryTypeForScope(scope),
      boundaryRefId: '',
      planningEstimates: [],
    }));
    setPreviews({});
  };

  useEffect(() => {
    window.clearTimeout(previewTimerRef.current);
    if (isRetirement(draft) || isOtherClimateBenefit(draft)) {
      setPreviews({});
      return undefined;
    }
    const targets = draft.planningEstimates
      .filter(estimate => {
        const reduction = Number(estimate.reductionTco2ePerYear);
        return reduction && reduction > 0 && draft.category;
      })
      .map(estimate => estimate.fiscalYear);
    setPreviews(current => Object.fromEntries(
      Object.entries(current).filter(([year]) => targets.includes(Number(year))),
    ));
    if (targets.length === 0) return undefined;
    previewTimerRef.current = window.setTimeout(() => {
      const sequence = previewSequenceRef.current + 1;
      previewSequenceRef.current = sequence;
      Promise.all(targets.map(fiscalYear => (
        estimateActionPlanPreview({
          scope: draft.scope,
          category: draft.category,
          sub_category: draft.subCategory || null,
          boundary_type: draft.boundaryType,
          boundary_ref_id: draft.boundaryRefId || null,
          fiscal_year: fiscalYear,
          expected_reduction_tco2e: String(Number(
            draft.planningEstimates.find(estimate => estimate.fiscalYear === fiscalYear)
              ?.reductionTco2ePerYear || 0,
          )),
        })
          .then(payload => ({ fiscalYear, payload }))
          .catch(() => ({ fiscalYear, payload: null }))
      ))).then(results => {
        if (previewSequenceRef.current !== sequence) return;
        const nextPreviews = {};
        results.forEach(({ fiscalYear, payload }) => {
          if (payload) nextPreviews[fiscalYear] = payload;
        });
        setPreviews(current => ({ ...current, ...nextPreviews }));
      });
    }, 500);
    return () => window.clearTimeout(previewTimerRef.current);
  }, [draft]);

  const boundaryOptions = useMemo(() => {
    if (draft.boundaryType === 'LEGAL_ENTITY') {
      return legalEntities.map(entity => ({
        value: entity.company_code,
        label: entity.company_name_en || entity.company_name_jp || entity.company_code,
      }));
    }
    if (draft.boundaryType === 'LOCATION') {
      return offices.map(office => ({
        value: office.location_code,
        label: office.location_name_en || office.location_name_jp || office.location_code,
      }));
    }
    return [];
  }, [draft.boundaryType, legalEntities, offices]);

  const validate = () => {
    if (!draft.title.trim()) return t('climateAction.plan.dialog.nameRequired');
    if (!isRetirement(draft) && !isOtherClimateBenefit(draft) && !draft.scope) {
      return t('climateAction.plan.dialog.scopeRequired');
    }
    if (!isRetirement(draft) && !isOtherClimateBenefit(draft) && !draft.category) {
      return t('climateAction.plan.dialog.categoryRequired');
    }
    if (!isRetirement(draft) && !isOtherClimateBenefit(draft)
      && scopeSubCategories.length > 0 && !draft.subCategory) {
      return t('climateAction.plan.dialog.subCategoryRequired', {
        category: CATEGORY_META[draft.category]?.short || draft.category,
      });
    }
    if (draft.scope === 'SCOPE_2' && !String(draft.boundaryRefId || '').trim()) {
      return t('climateAction.plan.dialog.boundaryRequired');
    }
    if (!draft.startFiscalYear || !draft.endFiscalYear) {
      return t('climateAction.plan.dialog.yearRequired');
    }
    const invalidReduction = draft.planningEstimates.some(estimate => {
      const reduction = estimate.reductionTco2ePerYear;
      return reduction !== '' && reduction != null && Number(reduction) < 0;
    });
    if (invalidReduction) return t('climateAction.plan.dialog.reductionRequired');
    return null;
  };

  const handleSave = async () => {
    setAttemptedSave(true);
    const validationError = validate();
    if (validationError) {
      setSaveError(validationError);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const roundedDraft = {
        ...draft,
        planningEstimates: draft.planningEstimates.map(estimate => ({
          ...estimate,
          reductionTco2ePerYear: roundReduction(estimate.reductionTco2ePerYear),
        })),
      };
      await onSave(roundedDraft, plan?.id || null);
      onRefresh?.();
      onClose();
    } catch (error) {
      onError?.(error);
    } finally {
      setSaving(false);
    }
  };

  const boundaryPicker = isRetirement(draft) || isOtherClimateBenefit(draft)
    || draft.boundaryType === 'COMPANY' ? null : (
    <Grid item xs={12} md={6}>
      <LabeledField label={t('climateAction.plan.dialog.boundary')} required={draft.scope === 'SCOPE_2'}>
        <Autocomplete
          clearIcon={draft.boundaryRefId ? <CloseRoundedIcon fontSize="small" /> : null}
          disabled={identityLocked}
          freeSolo
          getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
          isOptionEqualToValue={(option, value) => option.value === value}
          onChange={(event, option) => setIdentityField('boundaryRefId', option?.value || option || '')}
          onInputChange={(event, value) => setField('boundaryRefId', value)}
          options={boundaryOptions}
          renderOption={(props, option, state) => (
            <li {...props} key={`${option.value}-${state.index}`}>{option.label}</li>
          )}
          renderInput={params => <TextField {...params} size="small" />}
          sx={{
            '& .MuiAutocomplete-popper .MuiAutocomplete-listbox': {
              maxHeight: 320,
              overflowY: 'auto',
              scrollbarWidth: 'thin',
            },
          }}
          value={boundaryOptions.find(option => option.value === draft.boundaryRefId) || draft.boundaryRefId}
        />
      </LabeledField>
    </Grid>
  );

  return (
      <AppDialog
        fullWidth
        maxWidth={'lg'}
        onClose={saving ? undefined : onClose}
        open={open}
        PaperProps={{ sx: { height: { xs: 'calc(100dvh - 24px)', md: 760 }, maxHeight: 'calc(100dvh - 24px)' } }}
      >
      <DialogTitle sx={{ pr: 7 }}>
        {t(plan ? 'climateAction.plan.dialog.editTitle' : 'climateAction.plan.dialog.createTitle')}
        <IconButton aria-label={t('common.close')} disabled={saving} onClick={onClose} sx={{ position: 'absolute', right: 18, top: 18 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ flex: 1, height: { xs: 'auto', md: 620 }, minHeight: 0, overflowY: 'auto' }}>
        <Tabs
          onChange={(event, nextTab) => setTab(nextTab)}
          sx={{ mb: 2, '& .MuiTab-root': { flex: 1, maxWidth: 'none', textTransform: 'none' } }}
          value={tab}
        >
          <Tab label={t('climateAction.plan.dialog.detailsTab')} />
          <Tab label={t('climateAction.plan.dialog.estimateTab')} />
        </Tabs>

        {tab === 0 && (
          <Grid container spacing={2.5}>
             <Grid item xs={12} md={6}>
              <LabeledField label={t('climateAction.plan.dialog.name')} required>
                <TextField
                  fullWidth
                  onChange={event => setField('title', event.target.value)}
                  size={'small'}
                  value={draft.title}
                />
              </LabeledField>
            </Grid>

             <Grid item xs={12} md={6}>
               <LabeledField label={t('climateAction.plan.dialog.contributionType')} required>
                 <TextField
                   fullWidth
                   disabled={identityLocked}
                   onChange={event => {
                     const contributionType = event.target.value;
                     setDraft(current => ({
                       ...current,
                       contributionType,
                       actionType: OPERATIONAL,
                       scope: contributionType === OTHER_CLIMATE_BENEFIT ? 'OTHER' : 'SCOPE_1',
                       category: contributionType === OTHER_CLIMATE_BENEFIT ? 'POLICY' : 'GASOLINE',
                       subCategory: null,
                       boundaryType: contributionType === OTHER_CLIMATE_BENEFIT
                         ? 'COMPANY' : 'LEGAL_ENTITY',
                       boundaryRefId: '',
                       planningEstimates: [],
                     }));
                     setPreviews({});
                   }}
                   select
                   size="small"
                   value={isRetirement(draft) ? EMISSIONS_REDUCTION : draft.contributionType}
                   SelectProps={{ MenuProps: SCROLLABLE_SELECT_MENU_PROPS }}
                 >
                    {ACTION_PLAN_CONTRIBUTION_TYPES.map(option => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                 </TextField>
               </LabeledField>
             </Grid>

            <Grid item xs={12}>
              <LabeledField label={t('climateAction.plan.dialog.description')}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  onChange={event => setField('description', event.target.value)}
                  size={'small'}
                  value={draft.description}
                />
              </LabeledField>
            </Grid>

             {!isRetirement(draft) && !isOtherClimateBenefit(draft) && (
              <>
             <Grid item xs={12} md={6}>
                  <LabeledField label={t('climateAction.plan.dialog.scope')} required>
                    <TextField
                      disabled={identityLocked}
                      fullWidth
                      onChange={event => changeScope(event.target.value)}
                      select
                      size={'small'}
                      value={draft.scope}
                       SelectProps={{ MenuProps: SCROLLABLE_SELECT_MENU_PROPS }}
                    >
                      {SCOPE_OPTIONS.map(scope => (
                         <MenuItem key={scope} value={scope}>
                           {ACTION_PLAN_SCOPE_LABELS[scope] || scope}
                         </MenuItem>
                      ))}
                    </TextField>
                  </LabeledField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <LabeledField label={t('climateAction.plan.dialog.category')} required>
                    <TextField
                      disabled={identityLocked}
                      fullWidth
                      onChange={event => setIdentityField('category', event.target.value)}
                      select
                      size={'small'}
                      value={draft.category}
                       SelectProps={{ MenuProps: SCROLLABLE_SELECT_MENU_PROPS }}
                    >
                      {scopeCategoryOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </LabeledField>
                </Grid>
                {scopeSubCategories.length > 0 && (
                  <Grid item xs={12} md={6}>
<LabeledField label={t('climateAction.plan.dialog.subCategory')} required>
                        <TextField
                          disabled={identityLocked}
                          error={attemptedSave && subCategoryMissing}
                          fullWidth
                          helperText={attemptedSave && subCategoryMissing ? t('climateAction.plan.dialog.subCategoryRequired', {
                            category: CATEGORY_META[draft.category]?.short || draft.category,
                          }) : undefined}
                          onChange={event => setIdentityField('subCategory', event.target.value)}
                          select
                          size={'small'}
                          value={draft.subCategory || ''}
                           SelectProps={{ MenuProps: SCROLLABLE_SELECT_MENU_PROPS }}
                        >
                        {scopeSubCategories.map(subCategory => (
                          <MenuItem key={subCategory} value={subCategory}>
                            {SUB_CATEGORY_LABELS_BY_CATEGORY[draft.category]?.[subCategory] || subCategory}
                          </MenuItem>
                        ))}
                      </TextField>
                    </LabeledField>
                  </Grid>
                )}
              </>
            )}

             {boundaryPicker}

             <Grid item xs={12}>
               <Grid container spacing={2.5}>
                 <Grid item xs={12} md={6}>
                   <LabeledField label={t('climateAction.plan.dialog.startFiscalYear')} required>
                     <TextField
                        disabled={identityLocked}
                       fullWidth
                       onChange={event => setField('startFiscalYear', Number(event.target.value))}
                       select
                       size="small"
                       value={draft.startFiscalYear}
                       SelectProps={{ MenuProps: SCROLLABLE_SELECT_MENU_PROPS }}
                       sx={{ '& .MuiOutlinedInput-notchedOutline': { borderRadius: 0.5 } }}
                     >
                       {startOptions.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                     </TextField>
                   </LabeledField>
                 </Grid>
                 <Grid item xs={12} md={6}>
                   <LabeledField label={t('climateAction.plan.dialog.endFiscalYear')} required>
                     <TextField
                       fullWidth
                       onChange={event => setField('endFiscalYear', Number(event.target.value))}
                       select
                       size="small"
                       value={draft.endFiscalYear}
                       SelectProps={{ MenuProps: SCROLLABLE_SELECT_MENU_PROPS }}
                       sx={{ '& .MuiOutlinedInput-notchedOutline': { borderRadius: 0.5 } }}
                     >
                       {endOptions.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                     </TextField>
                   </LabeledField>
                 </Grid>
               </Grid>
             </Grid>
          </Grid>
        )}

         {tab === 1 && (
          <Stack spacing={1.5}>
             <Typography color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
               {t('climateAction.plan.dialog.formulaDescription')}
             </Typography>
             {draft.scope === 'SCOPE_2' && draft.category === 'ELECTRICITY' && (
               <Alert severity="info" sx={{ borderRadius: 0.5 }}>
                 {t('climateAction.plan.dialog.greenElectricityEstimateNote')}
               </Alert>
             )}

             <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 0.5, overflow: 'hidden' }}>
              <Stack
                direction={'row'}
                sx={{
                  bgcolor: 'background.default',
                  borderBottom: 1,
                  borderColor: 'divider',
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography sx={{ flex: '0 0 90px', fontSize: 12, fontWeight: 700 }}>
                  {t('climateAction.plan.dialog.yearColumn')}
                </Typography>
                <Typography sx={{ flex: '0 0 180px', fontSize: 12, fontWeight: 700 }}>
                  {t(isRetirement(draft)
                    ? 'climateAction.plan.dialog.plannedRetirementQuantity'
                    : 'climateAction.plan.dialog.expectedReduction')}
                </Typography>
                  <Typography component="div" sx={{ flex: '0 0 240px', fontSize: 12, fontWeight: 700 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {t('climateAction.plan.dialog.estimateColumn')}
                      <Tooltip title={t('climateAction.plan.dialog.estimateInfo')}>
                        <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                      </Tooltip>
                    </Stack>
                </Typography>
                 <Typography sx={{ flex: 1, minWidth: 220, fontSize: 12, fontWeight: 700 }}>
                  {t('climateAction.plan.dialog.basisNote')}
                </Typography>
              </Stack>
              {tableYears
                .slice(estimatePage * TABLE_PAGE_SIZE, (estimatePage + 1) * TABLE_PAGE_SIZE)
                .map(year => {
                const estimate = estimateForYear(year);
                const preview = previews[year];
                return (
                   <Stack
                    direction={'row'}
                    key={year}
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      px: 1.5,
                      py: 1,
                       '&:last-child': { borderBottom: 0 },
                       borderRadius: 0,
                    }}
                  >
                    <Typography sx={{ flex: '0 0 90px', fontSize: 13.5, fontWeight: 700, pt: 1 }}>
                      {year}
                    </Typography>
                    <Stack sx={{ flex: '0 0 180px', pr: 1 }}>
                       <TextField
                        onChange={event => setEstimateField(year, 'reductionTco2ePerYear', event.target.value)}
                        size={'small'}
                        sx={{ '& .MuiOutlinedInput-notchedOutline': { borderRadius: 0.5 } }}
                        type={'number'}
                        value={estimate.reductionTco2ePerYear}
                      />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: '0 0 240px', pr: 1, pt: 1 }}>
                      <Typography sx={{ fontSize: 13 }}>
                        {preview?.convertible
                          ? `${formatTonne(preview.activityEquivalent || preview.activity_equivalent, { withUnit: false })} ${preview.activityUnit || preview.activity_unit || ''}`
                          : '—'}
                      </Typography>
                      {preview?.convertible && (
                        <Tooltip
                          title={(
                            <Stack spacing={0.25}>
                              <Typography component="span" sx={{ fontSize: 12, color: 'inherit' }}>
                                {preview.formula_note || preview.formulaNote || 'Expected tCO₂e / EF = activity equivalent'}
                              </Typography>
                              <Typography component="span" sx={{ fontSize: 12, color: 'inherit' }}>
                                EF: {preview.factor_value || preview.factorValue} {preview.factor_unit || preview.factorUnit}
                              </Typography>
                              <Typography component="span" sx={{ fontSize: 12, color: 'inherit' }}>
                                FY: {preview.factor_effective_year || preview.factorEffectiveYear || preview.effective_year}
                              </Typography>
                            </Stack>
                          )}
                        >
                          <InfoOutlinedIcon sx={{ color: 'text.secondary', cursor: 'help', fontSize: 16, ml: 0.5 }} />
                        </Tooltip>
                      )}
                    </Stack>
                    <TextField
                      inputProps={{ maxLength: 1000 }}
                      onChange={event => setEstimateField(year, 'basisNote', event.target.value)}
                      size={'small'}
                       sx={{ flex: 1, minWidth: 220, '& .MuiOutlinedInput-notchedOutline': { borderRadius: 0.5 } }}
                      value={estimate.basisNote}
                    />
                  </Stack>
                );
              })}
            </Box>
            {tableYears.length > TABLE_PAGE_SIZE && (
              <TablePagination
                component="div"
                count={tableYears.length}
                onPageChange={(event, nextPage) => setEstimatePage(nextPage)}
                page={estimatePage}
                rowsPerPage={TABLE_PAGE_SIZE}
                rowsPerPageOptions={[TABLE_PAGE_SIZE]}
                labelRowsPerPage={t('common.rowsPerPage')}
              />
            )}
          </Stack>
         )}

         {saveError && <Alert severity={'error'} sx={{ mt: 2 }}>{saveError}</Alert>}
      </DialogContent>
<DialogActions sx={{ px: 3, py: 2 }}>
         <Button color={'inherit'} disabled={saving} onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button disabled={saving || !canMutate} onClick={handleSave} startIcon={saving ? <CircularProgress color="inherit" size={15} /> : undefined} variant={'contained'}>
           {plan ? t('common.update') : t('common.save')}
        </Button>
       </DialogActions>

     </AppDialog>
  );
}
